import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

type OutcomeType =
  | 'resolved'
  | 'unresolved_abandoned_by_customer'
  | 'unresolved_attendant_gave_up'
  | 'unresolved_blocked_by_system'
  | 'unresolved_out_of_scope'
  | 'not_applicable';

/**
 * useConversationLifecycle — Fatia F.
 *
 * Mutations REST para close/pause/resume/reopen/archive. Invalida cache
 * de conversation detail após cada operação.
 */
export function useConversationLifecycle(conversationId: string | undefined): {
  close: (body: {
    outcomeType: OutcomeType;
    outcomeReason?: string;
    notes?: string;
    overrideJustification?: string;
  }) => Promise<{
    conversationId: string;
    status: string;
    outcomeId: string;
    warnings: string[];
  }>;
  pause: (pausedReason: string) => Promise<{ status: string }>;
  resume: (
    resumeTo?: 'awaiting_attendant' | 'awaiting_customer' | 'in_progress',
  ) => Promise<{ status: string }>;
  reopen: () => Promise<{ status: string; mode: 'reopen-same' }>;
  archive: () => Promise<{ status: string }>;
  isClosing: boolean;
  isPausing: boolean;
  isResuming: boolean;
  isReopening: boolean;
  isArchiving: boolean;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    if (conversationId === undefined) return;
    void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    // A lista de inbox usa outra queryKey (['conversations', filters]) — sem isto
    // a conversa encerrada permanecia na lista com status velho.
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  };

  const closeMutation = useMutation({
    mutationFn: (body: {
      outcomeType: OutcomeType;
      outcomeReason?: string;
      notes?: string;
      overrideJustification?: string;
    }) => {
      if (conversationId === undefined) throw new Error('conversationId missing');
      return api.closeConversation(conversationId, body);
    },
    onSuccess: invalidate,
  });

  const pauseMutation = useMutation({
    mutationFn: (pausedReason: string) => {
      if (conversationId === undefined) throw new Error('conversationId missing');
      return api.pauseConversation(conversationId, pausedReason);
    },
    onSuccess: invalidate,
  });

  const resumeMutation = useMutation({
    mutationFn: (resumeTo?: 'awaiting_attendant' | 'awaiting_customer' | 'in_progress') => {
      if (conversationId === undefined) throw new Error('conversationId missing');
      return api.resumeConversation(conversationId, resumeTo);
    },
    onSuccess: invalidate,
  });

  const reopenMutation = useMutation({
    mutationFn: () => {
      if (conversationId === undefined) throw new Error('conversationId missing');
      return api.reopenConversation(conversationId);
    },
    onSuccess: invalidate,
  });

  const archiveMutation = useMutation({
    mutationFn: () => {
      if (conversationId === undefined) throw new Error('conversationId missing');
      return api.archiveConversation(conversationId);
    },
    onSuccess: invalidate,
  });

  return {
    close: closeMutation.mutateAsync,
    pause: (reason: string): Promise<{ status: string }> => pauseMutation.mutateAsync(reason),
    resume: (
      to?: 'awaiting_attendant' | 'awaiting_customer' | 'in_progress',
    ): Promise<{ status: string }> => resumeMutation.mutateAsync(to),
    reopen: (): Promise<{ status: string; mode: 'reopen-same' }> => reopenMutation.mutateAsync(),
    archive: (): Promise<{ status: string }> => archiveMutation.mutateAsync(),
    isClosing: closeMutation.isPending,
    isPausing: pauseMutation.isPending,
    isResuming: resumeMutation.isPending,
    isReopening: reopenMutation.isPending,
    isArchiving: archiveMutation.isPending,
  };
}
